import { NgIf } from '@angular/common';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, Component, ElementRef, Input, Optional, Self, ViewChild } from '@angular/core';

import { DsAbstractControlComponent } from '@abstract/components/ds-abstract-control.component';

/**
 * Reusable textarea component for Angular Material with support for:
 * - reactive forms (`FormControl` / `NgControl`)
 * - custom error state handling (immediate error display)
 * - prefix/suffix content projection
 * - CDK textarea autosize (`cdkTextareaAutosize`)
 * - character limits via `maxlength` / `minlength`
 * - host `max-height` inheritance for consumer-controlled scroll height
 *
 * Automatically integrates with Angular Material's `<mat-form-field>`
 * error styling and supports showing icons or hints based on the form control state.
 *
 * @remarks Doesn't support ngModel
 */
@Component({
  selector: 'ds-textarea',
  templateUrl: './ds-textarea.component.html',
  styleUrls: ['./ds-textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, TextFieldModule, MatIconModule, MatInputModule, ReactiveFormsModule],
})
export class DsTextareaComponent extends DsAbstractControlComponent {
  @ViewChild('textarea') private readonly textareaRef?: ElementRef<HTMLTextAreaElement>;

  /** Whether the textarea is readonly */
  @Input() readonly = false;

  /** HTML autocomplete attribute */
  @Input() autocomplete?: string;

  /** HTML name attribute */
  @Input() name?: string;

  /** Maximum allowed length for textarea value */
  @Input() maxlength?: number;

  /** Minimum allowed length for textarea value */
  @Input() minlength?: number;

  /** Fixed row count when autosize is disabled */
  @Input() rows = 3;

  /** Enables CDK `cdkTextareaAutosize` */
  @Input() isAutosize = true;

  /** Minimum rows used by CDK textarea autosize */
  @Input() minRows = 3;

  /** Maximum rows used by CDK textarea autosize */
  @Input() maxRows = 8;

  /** Whether the textarea is disabled */
  @Input()
  set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;

    if (!this.ngControl && isDisabled) {
      this.defaultControl.disable();
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  private _disabled = false;

  /** Internal textarea value */
  @Input()
  set value(val: string | null) {
    this._value = val ?? '';

    if (!this.ngControl) {
      this.defaultControl.setValue(this._value, { emitEvent: false });
    }
  }

  get value(): string {
    return this._value;
  }

  private _value = '';

  readonly defaultControl = new FormControl('');

  constructor(@Self() @Optional() public ngControl: NgControl) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  /**
   * Writes a value from the forms API into the control.
   *
   * @param obj - value provided by Angular forms
   */
  writeValue(obj: string | null): void {
    this._value = obj ?? '';

    if (!this.ngControl) {
      this.defaultControl.setValue(this._value, { emitEvent: false });
    }

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Updates the disabled state from the forms API.
   *
   * @param isDisabled - whether the control should be disabled
   */
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Handles native textarea input and propagates the value to the forms API.
   *
   * @param value - current textarea value
   */
  handleInput(value: string): void {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  /**
   * Focuses the native textarea element.
   */
  focus(): void {
    this.textareaRef?.nativeElement.focus();
  }

  get control(): FormControl {
    return (this.ngControl?.control ?? this.defaultControl) as FormControl;
  }
}
